import React from "react";
import { Link } from "gatsby";
import get from "lodash/get";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Flex, Box } from "reflexbox";
import { OutboundLink } from "gatsby-plugin-google-gtag";

import { Copy } from "../components/Copy";
import { Heading } from "../components/Heading";
import * as colors from "../styles/colors";

export const defaultContentOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => (
      <Box as="span" fontWeight={600}>
        {text}
      </Box>
    )
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Box mb={[30, 40]}>
        <Copy>{children}</Copy>
      </Box>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <Box mb={[30, 50]} as="ul">
        {children}
      </Box>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <Box mb={[30, 50]} as="ul">
        <Copy as="ol">{children}</Copy>
      </Box>
    ),
    [BLOCKS.QUOTE]: (_, children) => (
      <Box
        mb={[30, 50]}
        style={{
          fontStyle: "italic",
          position: "relative"
        }}
      >
        <Box
          style={{
            position: "absolute",
            borderLeft: `4px solid ${colors.muted}`,
            height: "100%",
            left: -28
          }}
        ></Box>
        <Copy>{children}</Copy>
      </Box>
    ),
    [BLOCKS.HEADING_1]: (_, children) => (
      <Heading type="h1">{children}</Heading>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading type="h2">{children}</Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading type="h3">{children}</Heading>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { gatsbyImageData, title, url } = node.data.target;
      const image = getImage(gatsbyImageData);

      const gif =
        node.data.target?.gatsbyImageData?.images?.sources?.find(
          (source) => source.type === "image/gif"
        );

      if (gif) {
        const src = gif?.srcSet?.split(",")?.[2]?.split?.(" ")?.[0];
        return (
          <Box mb={[50]}>
            <img src={src} alt={title} title={title} width="100%" />
          </Box>
        );
      }

      const endsWith = (string, fragment) =>
        string.indexOf(fragment) === string.length - fragment.length;

      const isVideo =
        url && typeof url === "string" && endsWith(url, ".mp4");

      if (isVideo) {
        return (
          <Flex
            mb={[50]}
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Box
              as="video"
              width={[1, 2 / 3, 1 / 2]}
              preload="auto"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={url} type="video/mp4" />
              Your browser does not support HTML5 video.
            </Box>
          </Flex>
        );
      }

      if (!image) {
        return null;
      }

      return (
        <Box mb={[50]}>
          <GatsbyImage image={image} alt={title} title={title} />
        </Box>
      );
    }
  }
};

export const termsOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => (
      <Box as="span" fontWeight={600}>
        {text}
      </Box>
    )
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Box mb={[25]}>
        <Copy>{children}</Copy>
      </Box>
    ),
    [BLOCKS.HEADING_1]: (_, children) => (
      <Heading type="h1">{children}</Heading>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading type="h2">{children}</Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading type="h3">{children}</Heading>
    )
  }
};

export const announcementBarOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => (
      <Box as="span" fontWeight={600}>
        {text}
      </Box>
    ),
    [MARKS.ITALIC]: (text) => <Box as="em">{text}</Box>,
    [MARKS.UNDERLINE]: (text) => (
      <Box as="span" textDecoration="underline">
        {text}
      </Box>
    )
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Box as="span">{children}</Box>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const uri = node?.data?.uri;

      if (uri.startsWith("http")) {
        return (
          <OutboundLink
            href={uri}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </OutboundLink>
        );
      } else {
        return <Link to={uri}>{children}</Link>;
      }
    }
  }
};

const shortText = (data) => (name) => get(data, [name], "");

const bool = (data) => (name) => get(data, [name], false);

const longText = (data) => (name) =>
  get(data, [name, name], "")
    .split("\n")
    .map((line, i) =>
      line.length ? (
        <span key={line + i}>
          {line}
          <br />
        </span>
      ) : (
        <span key={line + i} className="break-sm" />
      )
    );

const image =
  (data) =>
  (path, defaultValue = null) =>
    get(data, path, defaultValue);

const json =
  (data) =>
  (name, customOptions = {}) => {
    if (customOptions === "termsOptions") {
      customOptions = termsOptions;
    }

    if (customOptions === "announcementBarOptions") {
      customOptions = announcementBarOptions;
    }

    const options = {
      ...defaultContentOptions,
      ...customOptions,
      renderMark: {
        ...(defaultContentOptions?.renderMark ?? {}),
        ...(customOptions?.renderMark ?? {})
      },
      renderNode: {
        ...(defaultContentOptions?.renderNode ?? {}),
        ...(customOptions?.renderNode ?? {})
      }
    };

    const raw = get(data, name, "");
    return renderRichText(raw, options);
  };

export const content = (contentType) => ({
  shortText: shortText(contentType),
  longText: longText(contentType),
  image: image(contentType),
  bool: bool(contentType),
  json: json(contentType),
  get: (path, defaultValue = null) =>
    get(contentType, path, defaultValue)
});
